import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from 'react-dom';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import generalApi from "../../api/generalApi";
import LanguageSelector from "../common/LanguageSelector";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";

const AddProjectModal = ({ open, onClose, onProjectAdded, defaultLanguage }) => {
  const { language: preferredLanguage } = useLanguage();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [language, setLanguage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when modal opens with a new state
  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      
      // Priority: 1. defaultLanguage (explicitly passed), 2. user's preferredLanguage from context
      const selectedLang = defaultLanguage || preferredLanguage || "en-US";
      console.log("🚨 Setting language in modal:", { 
        defaultLanguage, 
        preferredLanguage, 
        selectedLang 
      });
      
      setLanguage(selectedLang);
      setError(null);
    }
  }, [open, defaultLanguage, preferredLanguage]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
    
    switch (field) {
      case "name":
        setName(value);
        break;
      case "description":
        setDescription(value);
        break;
      case "language":
        console.log("🚨 Language changed to:", value);
        setLanguage(value);
        break;
      default:
        break;
    }
  };

  const validateForm = () => {
    if (!name.trim()) {
      setError("Project name is required");
      return false;
    }

    if (!language) {
      setError("Language is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      const projectData = {
        name: name.trim(),
        description: description.trim(),
        language: language
      };

      // Make API call to create project
      console.log("🚀 Creating project...");
      const response = await generalApi.project.createProject(projectData);
      
      // Extract the project data from the response
      const createdProject = response.data || response;
      console.log("✅ Project created:", createdProject);
      
      // Show success message and reset form fields
      toast.success("Project created successfully!");
      setName("");
      setDescription("");
      
      // Call parent's callback with the created project and WAIT for it to complete
      if (onProjectAdded && createdProject) {
        console.log("🔄 Calling parent's onProjectAdded callback...");
        try {
          await onProjectAdded(createdProject);
          console.log("✅ Parent's callback completed");
        } catch (error) {
          console.error("❌ Error in parent's callback:", error);
          throw error; // Re-throw to be caught by outer catch block
        }
      }
      
      // Only close the modal after everything above is complete
      console.log("🚪 Closing modal...");
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error("Error creating project:", err);
      let errorMessage = "Failed to create project";
      
      // Detailed error handling
      if (err.response) {
        if (err.response.status === 402) {
          errorMessage = "Subscription required to create more projects.";
          setError(errorMessage);
          toast.error(errorMessage);
          if (onClose) {
            onClose();
          }
          return;
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.message || "Invalid project data";
        } else if (err.response.status === 409) {
          errorMessage = "A project with this name already exists";
        } else if (err.response.status === 401) {
          errorMessage = "Please log in to create a project";
        } else {
          errorMessage = `Server error (${err.response.status}): ${err.response.data?.message || "Please try again"}`;
        }
      } else if (err.request) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Use ReactDOM.createPortal to render this modal outside the normal DOM hierarchy
  return ReactDOM.createPortal(
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      closeAfterTransition
      disableEscapeKeyDown={true}  // Always disable escape key
      disableRestoreFocus={false}
      onBackdropClick={undefined}  // Disable backdrop click
      className="add-project-modal"
      sx={{ 
        zIndex: 1000000000,
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            position: 'relative',
            '& .MuiAutocomplete-popper': {
              position: 'fixed'  // This should force the dropdown to break out of the modal's stacking context
            }
          }
        }
      }}
    >
      <DialogTitle>Create New Project</DialogTitle>
      <Divider />
      
      <DialogContent sx={{ pb: 1, pt: 3 }}>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} id="create-project-form">
          <TextField
            label="Project Name"
            value={name}
            onChange={handleChange("name")}
            fullWidth
            required
            error={error && !name.trim()}
            helperText={error && !name.trim() ? "Project name is required" : ""}
            disabled={loading}
            sx={{ mb: 3 }}
            inputProps={{ "data-testid": "project-name-input" }}
          />
          
          <TextField
            label="Description (Optional)"
            value={description}
            onChange={handleChange("description")}
            fullWidth
            multiline
            rows={2}
            disabled={loading}
            sx={{ mb: 3 }}
          />
          
          <LanguageSelector
            value={language}
            onChange={handleChange("language")}
            error={error && !language}
            helperText={error && !language ? "Language is required" : ""}
            disabled={loading}
            PopperProps={{
              style: { zIndex: 1500000000 },
              container: document.body
            }}
          />
          
          {error && !error.includes("name") && !error.includes("language") && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </form>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !name.trim() || !language}
          sx={{
            bgcolor: "#be58da",
            "&:hover": { bgcolor: "#a347bf" },
            position: "relative"
          }}
        >
          {loading ? (
            <>
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  left: "50%",
                  marginLeft: "-12px",
                }}
              />
              <span style={{ visibility: "hidden" }}>Create Project</span>
            </>
          ) : (
            "Create Project"
          )}
        </Button>
      </DialogActions>
    </Dialog>,
    document.body
  );
};

export default AddProjectModal;
